import React, { useEffect, useState } from 'react'
import { Drawer, Typography, Stack, TextField, Button, Box, InputLabel, Switch, IconButton } from '@mui/material'
import intl from 'components/i18n/ReactIntlWrapper'
import ColorPicker from 'components/ColorPicker'
import MuiIconPicker from 'components/MuiIconPicker/MuiIconPicker'
import { getIconByName } from 'components/MuiIconPicker'
import { Connection, MarkerType } from '@xyflow/react'
import { FloatingEdgeType } from './FloatingEdge'
import { useApi } from '../../../api/ApiProvider'
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider'
import Api from 'axiosApi/api'
import ChipSelector from './ChipSelector'
import { handleAPIError } from 'common/errorHandler';
import ErrorSummary from 'components/common/ErrorSummary'
import { Scope } from 'axiosApi/models/scope'
import LabelKeySelector from 'components/LabelKeySelector'
import { ScopeDTO } from 'axiosApi/models'
import { Delete, Save, Edit } from '@mui/icons-material'

type FloatingEdgeDrawerProps = {
    selectedEdge: FloatingEdgeType | Connection
    onClose: () => void
    disabled?: boolean
    saveEdge: (edge: FloatingEdgeType) => void
    deleteEdge: (edgeId: string) => void
}

const FloatingEdgeDrawer: React.FC<FloatingEdgeDrawerProps> = ({ selectedEdge, onClose, disabled, saveEdge, deleteEdge }) => {

    const isFloatingEdgeType = (edge: any): edge is FloatingEdgeType => {
        return (edge as FloatingEdgeType)?.data !== undefined;
    };
    const [selectedIcon, setSelectedIcon] = useState('');
    const [iconPickerOpen, setIconPickerOpen] = useState(false);
    const [actionName, setActionName] = useState('')
    const [labelKey, setLabelKey] = useState('')
    const [color, setColor] = useState('#000')
    const [scope, setScope] = useState<ScopeDTO>(null)
    const [errors, setErrors] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [allowComments, setAllowComments] = useState(false);
    const handleLabelKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => setLabelKey(event.target.value)
    const handleActionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => setActionName(event.target.value)

    const [scopes, setScopes] = useState<Scope[]>([])
    const fetchScopes = () => {
        setLoading(true)
        api.employeeApi.apiVversionScopeAllGet('1', 1, 200, {})
        .then((response) => {
            if (response.data.data === null) {
                setScopes(response.data.data.queryResult);
                
            } else {
                setScopes(response.data.data.queryResult);
            }
        })
        .catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        }).finally(() => {
            setLoading(false)
        });
    }

    useEffect(() => {
        
        const floatingEdge = selectedEdge as FloatingEdgeType;
        const connection = selectedEdge as Connection;
        if(isFloatingEdgeType(selectedEdge)){
            setEditMode(true)
            setActionName(floatingEdge?.data?.actionName)
            setLabelKey(floatingEdge?.data?.labelKey)
            setAllowComments(floatingEdge?.data?.AllowComments)
            setSelectedIcon(floatingEdge?.data?.Icon ? floatingEdge?.data?.Icon : '')
            setColor(floatingEdge?.data?.DisplayColor ? floatingEdge?.data?.DisplayColor : '#000')
            setScope(scopes.find(x=>x.id == floatingEdge?.data?.scope));
        }else if(connection){
            setEditMode(false)
            setActionName('')
            setLabelKey('')
            setAllowComments(false)
            setSelectedIcon('')
            setColor('#000')
        }
    }, [selectedEdge])

    const [loading, setLoading] = useState(true)
    

    const api: Api = useApi();
    const toast = useToastMessageQueue();
    

    const value = (selectedEdge as FloatingEdgeType)?.data?.labelKey;

    const handleViewScopeChange = (newValue: any) => { setScope(newValue)}

    const handleSave = () => {
        setErrors({})
        if (!actionName) {
            setErrors({ summary: ['Action name is required']})
            return
        }
        saveEdge({
            ...selectedEdge as FloatingEdgeType,
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 30,
                height: 30,
                color: '#black',
            },
            label: labelKey,
            data: {
                actionName: actionName,
                labelKey: labelKey,
                Icon: selectedIcon,
                AllowComments: allowComments,
                DisplayColor: color,
                extraAttributes: (selectedEdge as FloatingEdgeType)?.data?.extraAttributes || {},
                scope: scope?.id
            },
            type: 'floating',
            id: editMode ? (selectedEdge as FloatingEdgeType)?.id : null
        })
    }

    const handleDelete = (edgeId: string) => {
        deleteEdge && deleteEdge(edgeId)
    };

    useEffect(() => {
        fetchScopes()
    }, [])

    return (
        <Drawer anchor="right" open={!!selectedEdge} onClose={onClose} classes={{ paper: 'drawer p-4' }} sx={{ '& .MuiDrawer-paper': { width: 500 } }}>
            <Box sx={{m:2}}>
            <Typography variant="h4" color="primary">
                {editMode ? "Edit Transition" : "Create Transition"}
            </Typography>
            <Stack spacing={2} mt={2} mb={2} className="floating-edge-form">
                <ErrorSummary errors={errors} showEverything={false}></ErrorSummary>
                <TextField
                    label={intl.get('editableNode.placeholder.nodeName')}
                    size="small"
                    type="text"
                    id="actionName"
                    name="actionName"
                    value={actionName}
                    onChange={handleActionNameChange}
                    disabled={disabled}
                    autoFocus
                    className="w-100"
                />
                <LabelKeySelector
                    label={intl.get('editableNode.placeholder.labelKey')}
                    size="small"
                    labelKey={labelKey}
                    setLabelKey={setLabelKey}
                    disabled={disabled}
                    className="w-100"
                />
                <Box>
                    <InputLabel>{intl.get('editableNode.placeholder.commentAllowed')}</InputLabel>
                    <Switch
                    classes={{
                        thumb: 'border border-gray'
                    }}
                    checked={allowComments} onChange={() => setAllowComments(!allowComments)} />
                </Box>
                <Box display={"flex"} flexDirection={'row'} gap={2}>
                    <Box sx={{ flex: 1 }}>
                        <InputLabel>{intl.get('editableNode.placeholder.icon')}</InputLabel>
                        <Box display="flex" gap={1} alignItems="center">
                            <IconButton
                                onClick={() => setIconPickerOpen(true)}
                                sx={{
                                    width: 56,
                                    height: 56,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                    }
                                }}
                            >
                                {selectedIcon && (() => {
                                    const IconComponent = getIconByName(selectedIcon);
                                    return IconComponent ? (
                                        <IconComponent 
                                            sx={{ 
                                                fontSize: 32,
                                                color: color 
                                            }} 
                                        />
                                    ) : <Edit />;
                                })() || <Edit />}
                            </IconButton>
                            {selectedIcon && (
                                <Typography variant="caption" color="text.secondary">
                                    {selectedIcon}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box>
                        <InputLabel>{intl.get('editableNode.placeholder.color')}</InputLabel>
                        <ColorPicker color={color} setColor={setColor} />
                    </Box>
                </Box>
                
                <MuiIconPicker
                    open={iconPickerOpen}
                    onClose={() => setIconPickerOpen(false)}
                    onSelect={(iconName) => setSelectedIcon(iconName)}
                    currentIcon={selectedIcon}
                    color={color}
                />
                <ChipSelector
                    label="Scope"
                    value={scope}
                    onChange={handleViewScopeChange}
                    scopes={scopes}
                    multiple={false}
                />
                <Box display={"flex"} gap={2} sx={{width: 'fit-content', alignSelf: 'end'}}>
                    {editMode &&
                        <Button startIcon={<Delete/>} variant="contained" color="error" onClick={() => {
                            handleDelete((selectedEdge as FloatingEdgeType)?.id)
                        }} sx={{width: 'fit-content', alignSelf: 'end'}} >
                            {intl.get('delete.button')}
                        </Button>
                    }
                    <Button startIcon={<Save/>} variant="contained" color="primary" onClick={() => handleSave()} sx={{width: 'fit-content', alignSelf: 'end'}}>
                        {intl.get('save.modal.save.button')}
                    </Button>
                </Box>
            </Stack>
        </Box>
        </Drawer>
    )
}

export default FloatingEdgeDrawer
